/**
 * Header
 *
 * Top navigation bar showing brand logo, global search (unless no-role),
 * notifications with live count via websocket, and a profile menu. The
 * notifications menu renders a scrollable list in a styled MUI Menu.
 */
import React, { useState, useEffect } from "react";
import { Box, Avatar, Badge } from "@mui/material";
import Menu from "@mui/material/Menu";
import logo from "../../../assets/images/LucyLogo.png";
import { getNoticationsByUserID } from "../../../api/notification";
import Cookies from "js-cookie";
import Profile from "../../profile/profile";
import "./header.scss";
import NotificationsPopup from "../../notifications/notifications";
import SearchWithTabs from "../../../common/components/search-with-tabs/search-with-tabs";
import LogOutTimerClock from "../../../common/components/logout-timer-clock/logout-timer-clock";
import NotificationIcon from "../../../assets/icons/bell.svg";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { useWebSocketNotifications } from "../../../common/hooks/websocketClient";

interface HeaderProps {
  isNoRole?: boolean;
}

const fetcher = async (userId: number) => {
  const response = await getNoticationsByUserID(userId);
  return response.data.data;
};

const Header: React.FC<HeaderProps> = ({ isNoRole }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationCountUpdate, setNotificationCountUpdate] =
    useState<any>(0);
  const [forceLogoutUpdate, setForceLogoutUpdate] = useState<any>(false);
  const [profilePhotoImage, setProfilePhotoImage] = useState<any>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] =
    useState<null | HTMLElement>(null);

  const userDetails = localStorage.getItem("userDetails")
    ? JSON.parse(localStorage.getItem("userDetails") || "{}")
    : null;

  const fullName = userDetails
    ? userDetails.firstName?.toUpperCase()?.[0] +
      userDetails.lastName?.toUpperCase()?.[0]
    : "";
  const getPhoto = async () => {
    const storedData = Cookies.get("userDetails") || "{}";
    const token = JSON.parse(storedData)?.fetchMSGraphAPI;
    const photoResponse = await fetch(
      "https://graph.microsoft.com/v1.0/me/photo/$value",
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const blob = await photoResponse.blob();
    const imageUrl = URL.createObjectURL(blob);
    localStorage.setItem("profileImageUrl", imageUrl);
    setProfilePhotoImage(imageUrl);
  };

  useEffect(() => {
    getPhoto();
  }, []);

  const getNotificationData = () => {
    if (userDetails?.employeeId) {
      fetcher(userDetails.employeeId).then((response) => {
        const notificationCountInfo = response
          ? response.filter((n: any) => !n.isRead).length
          : 0;
        setNotificationCountUpdate(notificationCountInfo);
      });
    }
  };

  useEffect(() => {
    getNotificationData();
  }, []);

  const userId = userDetails?.employeeId;
  const { notificationCount, forceLogout } = useWebSocketNotifications(userId);

  useEffect(() => {
    if (notificationCount !== null && notificationCount !== undefined) {
      setNotificationCountUpdate(notificationCount);
    }
  }, [notificationCount]);

  useEffect(() => {
    if (forceLogout !== null && forceLogout !== undefined) {
      setForceLogoutUpdate(forceLogout === "FORCE_LOGOUT");
    }
    console.log(forceLogout, "force-logout forceLogout");
  }, [forceLogout]);

  const handleProfileClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleProfileClose = () => setAnchorEl(null);
  const handleNotificationClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setNotificationAnchorEl(event.currentTarget);
  };
  const handleNotificationClose = () => setNotificationAnchorEl(null);

  return (
    <div className="dashboard-header">
      <div className="dashboard-header-content">
        <div className="dashboard-header-content-logo">
          <img
            src={logo}
            alt="Logo"
            className="dashboard-header-content-logo-img"
          />
        </div>

        <div className="dashboard-header-content-search">
          {!isNoRole && <SearchWithTabs />}
        </div>

        <div className="dashboard-header-content-notifications-profile">
          {!isNoRole && (
            <Badge
              badgeContent={notificationCountUpdate}
              color="error"
              overlap="circular"
              variant="dot"
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              onClick={handleNotificationClick}
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "8px",
                  height: "12px",
                  width: "12px",
                  minWidth: "10px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "2px",
                  lineHeight: 1,
                },
              }}
            >
              <Box
                sx={{
                  background: "#F6F6F6",
                  borderRadius: "50%",
                  padding: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <img src={NotificationIcon} alt="Notification" />
              </Box>
            </Badge>
          )}

          <Menu
            id="account-menu"
            anchorEl={notificationAnchorEl}
            open={Boolean(notificationAnchorEl)}
            onClose={handleNotificationClose}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 2.5,
                  borderRadius: "16px",
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "&::before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,

                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <OverlayScrollbarsComponent
              style={{ maxHeight: "454px", width: "400px" }}
              options={{
                scrollbars: {
                  theme: "os-theme-dark",
                  autoHide: "scroll",
                  autoHideDelay: 400,
                },
              }}
            >
              <NotificationsPopup
                onClose={handleNotificationClose}
                setNotificationCountUpdate={setNotificationCountUpdate}
              />
            </OverlayScrollbarsComponent>
          </Menu>

          <Badge onClick={handleProfileClick}>
            {profilePhotoImage ? (
              <Avatar src={profilePhotoImage}>{fullName}</Avatar>
            ) : (
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  fontWeight: 600,
                  fontFamily: "Manrope, sans-serif",
                  cursor: "pointer",
                  border: "2px solid #FFF",
                }}
                onClick={handleProfileClick}
              >
                {fullName}
              </Avatar>
            )}
          </Badge>

          <Menu
            id="account-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileClose}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 2.5,
                  backgroundColor: "#f7f8fb",
                  borderRadius: "16px",
                  scrollBehavior: "smooth",
                  WebkitOverflowScrolling: "touch",
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "&::before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <OverlayScrollbarsComponent
              style={{ maxHeight: "454px", width: "432px" }}
              options={{
                scrollbars: {
                  theme: "os-theme-dark",
                  autoHide: "scroll",
                  autoHideDelay: 400,
                },
              }}
            >
              <Profile employeeId={1} onClose={handleProfileClose} />
            </OverlayScrollbarsComponent>
          </Menu>
        </div>
      </div>
      {forceLogoutUpdate ? (
        <LogOutTimerClock forceLogoutUpdate={forceLogoutUpdate} />
      ) : (
        ""
      )}
    </div>
  );
};

export default Header;
